<template>
  <div
    data-component="UiSelect"
    class="relative"
    @mouseleave="modelShow = false"
    >
    <div class="relative">
      <ui-text
        :id="dataId"
        v-model="dataShowingText"
        v-model:mouseover="dataIsMouseOver"
        :title="dataShowingText"
        :type="type"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :label="label"
        data-component="UiTextSelect"
        :required="required"
        readonly
        :disabled="disabled"
        class="cursor-pointer"
        @click.capture.prevent.stop="modelShow = !modelShow"
        @touchstart.capture.prevent.stop="modelShow = !modelShow"
        />

      <span class="w-0 h-full absolute top-0 bottom-0 right-0" @click.capture.prevent.stop="modelShow = !modelShow">
        <i v-if="!modelShow" class="fas fa-angle-down absolute right-4 bottom-4"/>
        <i v-else class="fas fa-angle-up absolute right-4 bottom-4"/>
      </span>
    </div>

    <div v-show="modelShow" class="absolute w-full h-full">
      <div v-if="dataIsMounted" class="overflow-auto relative z-50 rounded-b-lg border border-gray-300 border-t-0 -mt-px backdrop-blur-xl shadow-md bg-gray-100/80">
        <slot />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
	id?: string
	label?: string
	type?: string
	error?: string
	autocomplete?: string
	placeholder?: string
	required?: boolean
	multiple?: boolean
  disabled?: boolean
}>()

const model = defineModel<string|string[]>({
  default: ''
})
const modelShow = defineModel<boolean>('show')

const dataIsMouseOver = ref<boolean>(false)
const dataIsMounted = ref<boolean>(false)
const dataIsInteractable = ref<boolean>(false)

const dataId = ref<string>()
const dataShowingText = ref<string>()
const dataRegisteredOptions = ref<Record<string|number,any>>({})

const dataDefaultText = ref<string|number>('')
const dataDefaultValue = ref<string|number>('')

onMounted(() => {
  dataId.value = props?.id ?? `${Math.random().toString(36).substring(7)}`

  model.value = (props.multiple && !Array.isArray(model.value))
    ? Object.assign([], model.value)
    : model.value as string

  nextTick(() => {
    dataIsMounted.value = true

    nextTick(() => nextTick(() => nextTick(() => {
      dataIsInteractable.value = true

      if (dataDefaultText.value || dataDefaultValue.value){
        methodSelectOption(
          dataDefaultText.value as string,
          dataDefaultValue.value as string
        )
      }
    })))
  })
})

const methodRegisterSelectOption = (
  text: string|undefined,
  value: string|undefined,
  is_default: boolean|undefined = false,
) => {
  dataRegisteredOptions.value[value ?? text ?? ''] = text ?? value ?? ''

  if(is_default){
    dataDefaultText.value = text ?? value ?? ''
    dataDefaultValue.value = value ?? text ?? ''
  }
}

const methodUnregisterSelectOption = (
  text: string|undefined,
  value: string|undefined,
) => {
  const newval: number|string = value ?? text ?? '';

  if(newval && dataRegisteredOptions.value[newval]){
    dataRegisteredOptions.value[newval] = undefined
  }

  nextTick(() => {
    if(props.multiple && Array.isArray(model.value)){
      model.value = model.value.filter((v: any) => {
        return v != newval
      })
    }else if(!props.multiple && model.value == newval){
      model.value = ''
    }

    nextTick(() => {
      methodRenderDisplay()
    })
  })

}

const methodSelectOption = (
  text: string|undefined,
  value: string|undefined,
) => {
  const newval = value ?? text;

  // if multiple and not array make it to an array
  if(props.multiple && !Array.isArray(model.value)){
    if(model.value) model.value = [model.value, newval as string]
    else model.value = []
  }

  if (props.multiple && Array.isArray(model.value)) {
    if(!model.value?.includes(newval as string)){
      model.value.push(newval as string)
    }else{
      model.value = model.value.filter((v: any) => {
        return v != newval
      })
    }
  } else if (!props.multiple) {
    model.value = newval as any
  }

  if(!props.multiple && dataIsInteractable.value){
    modelShow.value = false
  }

  nextTick(() => {
    methodRenderDisplay()
  })
}

const methodRenderDisplay = (flyout_afterwads: boolean = true) => {
  if(props.multiple){
    dataShowingText.value = (typeof model.value == 'string')
      ? model.value
      : ((model.value as string[]) ?? []).join(', ')

    if(dataIsInteractable.value){
      modelShow.value = (props.multiple && dataIsMouseOver.value)
        ? true
        : flyout_afterwads
    }
  }else{
    dataShowingText.value = dataRegisteredOptions.value[model.value as string] ?? model.value as string
  }
}

watch(() => dataRegisteredOptions.value, () => {
  if(!model.value) return;
  if(dataRegisteredOptions.value[model?.value as string]){
    dataShowingText.value = dataRegisteredOptions.value[model?.value as string]
  }
},{
  deep: true
})

watch(() => model.value, () => {
  methodRenderDisplay(false)
})

defineExpose({
  model,
  show: modelShow,
  is_multiple: props.multiple,
  methodSelectOption,
  methodRegisterSelectOption,
  methodUnregisterSelectOption
})
</script>
